import { z } from "zod";

/**
 * Client-side validation for the auth and account forms — QA sweep, 2026-08.
 *
 * The auth forms shipped with no validation at all beyond `type="email"` and
 * `required`, which QA filed against every one of them:
 *   - "密码validation需要做，另外密码提示例如8个字，需要大小写，英文数字等等"
 *   - "电邮没有格式验证，如果格式不正确没有提示"
 *   - "電話沒有驗證 如果是香港電話號碼必須要是八個位嘅數字"
 *   - "前端驗證 兩個密码是否完全相同"
 *   - "密碼驗證及提示" (twice — /auth/reset-password and /account/profile)
 *
 * `zod` was already a dependency with zero imports anywhere; this is the first
 * consumer. Everything here is a pure function, so it is trivially unit-testable
 * and shared by the four auth pages and the account profile modals.
 *
 * Messages are Traditional Chinese to match the rest of the auth copy.
 */

/** The password rules, exposed so the UI can render them as a live checklist. */
export const PASSWORD_RULES = [
  {
    id: "length",
    label: "至少 8 個字元",
    test: (v: string) => v.length >= 8,
  },
  {
    id: "case",
    label: "包含大寫及小寫英文字母",
    test: (v: string) => /[a-z]/.test(v) && /[A-Z]/.test(v),
  },
  {
    id: "alnum",
    label: "包含英文字母及數字",
    test: (v: string) => /[a-zA-Z]/.test(v) && /\d/.test(v),
  },
] as const;

export type PasswordRuleId = (typeof PASSWORD_RULES)[number]["id"];

/** Which rules a candidate password currently satisfies — drives the checklist. */
export function checkPasswordRules(value: string): Record<PasswordRuleId, boolean> {
  return PASSWORD_RULES.reduce(
    (acc, rule) => {
      acc[rule.id] = rule.test(value);
      return acc;
    },
    {} as Record<PasswordRuleId, boolean>
  );
}

export const emailSchema = z
  .string()
  .trim()
  .min(1, "請輸入電郵地址")
  .email("電郵格式不正確");

export const passwordSchema = z
  .string()
  .min(8, "密碼至少需要 8 個字元")
  .refine((v) => /[a-z]/.test(v) && /[A-Z]/.test(v), {
    message: "密碼需包含大寫及小寫英文字母",
  })
  .refine((v) => /[a-zA-Z]/.test(v) && /\d/.test(v), {
    message: "密碼需包含英文字母及數字",
  });

/**
 * Hong Kong numbers are exactly 8 digits and never start with 0 or 1.
 * Spaces and dashes are tolerated on input and stripped before checking, since
 * people habitually type "9123 4567".
 */
export const HK_DIAL_CODE = "+852";

export function normalisePhone(value: string): string {
  return value.replace(/[\s-]/g, "");
}

export const hkPhoneSchema = z
  .string()
  .transform(normalisePhone)
  .refine((v) => v.length > 0, { message: "請輸入電話號碼" })
  .refine((v) => /^\d+$/.test(v), { message: "電話號碼只可包含數字" })
  .refine((v) => v.length === 8, { message: "香港電話號碼必須為 8 位數字" })
  .refine((v) => /^[2-9]/.test(v), { message: "電話號碼格式不正確" });

/** Non-HK dial codes get a permissive length check only. */
export const intlPhoneSchema = z
  .string()
  .transform(normalisePhone)
  .refine((v) => v.length > 0, { message: "請輸入電話號碼" })
  .refine((v) => /^\d+$/.test(v), { message: "電話號碼只可包含數字" })
  .refine((v) => v.length >= 5 && v.length <= 15, {
    message: "電話號碼長度不正確",
  });

export function phoneSchemaFor(dialCode: string) {
  return dialCode === HK_DIAL_CODE ? hkPhoneSchema : intlPhoneSchema;
}

export const createAccountSchema = z
  .object({
    email: emailSchema,
    dialCode: z.string(),
    phone: z.string(),
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .superRefine((data, ctx) => {
    const phoneResult = phoneSchemaFor(data.dialCode).safeParse(data.phone);
    if (!phoneResult.success) {
      ctx.addIssue({
        code: "custom",
        path: ["phone"],
        message: phoneResult.error.issues[0]?.message ?? "電話號碼格式不正確",
      });
    }
    if (data.confirmPassword !== data.password) {
      ctx.addIssue({
        code: "custom",
        path: ["confirmPassword"],
        message: "兩次輸入的密碼不相同",
      });
    }
  });

export const resetPasswordSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .superRefine((data, ctx) => {
    if (data.confirmPassword !== data.password) {
      ctx.addIssue({
        code: "custom",
        path: ["confirmPassword"],
        message: "兩次輸入的密碼不相同",
      });
    }
  });

export const forgotPasswordSchema = z.object({ email: emailSchema });

/**
 * Flatten a ZodError into `{ field: firstMessage }`, which is what the forms
 * render. Only the first issue per field is shown — stacking three password
 * errors under one input is noise when the checklist above already itemises them.
 */
export function fieldErrors(error: z.ZodError): Record<string, string> {
  const out: Record<string, string> = {};
  for (const issue of error.issues) {
    const key = String(issue.path[0] ?? "");
    if (key && !(key in out)) out[key] = issue.message;
  }
  return out;
}

/** Dial codes offered by the country-code selector. */
export const DIAL_CODES = [
  { code: "+852", label: "香港 +852" },
  { code: "+853", label: "澳門 +853" },
  { code: "+86", label: "中國內地 +86" },
  { code: "+886", label: "台灣 +886" },
  { code: "+65", label: "新加坡 +65" },
  { code: "+44", label: "英國 +44" },
  { code: "+1", label: "美國/加拿大 +1" },
  { code: "+61", label: "澳洲 +61" },
] as const;
